import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { actions } from "features/builder/slice";
import { usePieceListScroll } from "features/builder/hook";
import { getTrashPieces, restorePiece, deletePieceForever } from "features/builder/api";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  PieceLayoutMode,
  comparePieces,
  toCardPiece,
} from "features/builder/pieceList";
import {
  MapCard,
  MapListItem,
  BuilderConfirmDialog,
  BuilderModalAccent,
  TrashMapsHeader,
  BuilderSidebar,
  SidebarSection,
  LayoutModeMenu,
  LAYOUT_GRID_CLASS,
} from "features/builder/elements";

export const TrashMapsTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const active = useSelector((state: RootState) => state.user.active);
  const [pieces, setPieces] = useState<MockPiece[]>([]);
  const { sortAxis, sortOrder, layoutMode } = useSelector(
    (state: RootState) => state.builder.listUi.trash,
  );
  const [deleteTarget, setDeleteTarget] = useState<MockPiece | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<MockPiece | null>(null);
  const { ref: scrollCallbackRef, onScroll } = usePieceListScroll(
    PieceListContext.TRASH,
    pieces.length > 0,
  );

  const load = useCallback(async () => {
    if (!active?.uid) return;
    try {
      const items = await getTrashPieces(active.uid);
      setPieces(items.map(toCardPiece));
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_LOAD_FAILED")}</p>
        </div>,
      );
    }
  }, [active?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleMaps = useMemo(() => {
    const list = [...pieces];
    list.sort(comparePieces(sortAxis, sortOrder));
    return list;
  }, [pieces, sortAxis, sortOrder]);

  const handleAction = (piece: MockPiece) => (action: PieceCardAction) => {
    switch (action) {
      case PieceCardAction.RESTORE:
        setRestoreTarget(piece);
        break;
      case PieceCardAction.DELETE_FOREVER:
        setDeleteTarget(piece);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !active?.uid) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deletePieceForever({ piece_id: target.piece_id, uid: active.uid });
      await load();
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_TRASH_DELETE_TOAST")}</p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget || !active?.uid) return;
    const target = restoreTarget;
    setRestoreTarget(null);
    try {
      await restorePiece({ piece_id: target.piece_id, uid: active.uid });
      await load();
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_TRASH_RESTORE_TOAST")}</p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  return (
    <div className="flex-row-el relative w-full h-full">
      <BuilderSidebar>
        <TrashMapsHeader
          count={visibleMaps.length}
          sortAxis={sortAxis}
          sortOrder={sortOrder}
          onChangeSort={(axis, order) =>
            dispatch(actions.setListSort({ context: PieceListContext.TRASH, axis, order }))
          }
        />
        <SidebarSection
          icon="grid_view"
          labelKey="MSG_BUILDER_SECTION_LAYOUT"
          accentClass="text-brown-80"
          divider={false}
        >
          <LayoutModeMenu
            value={layoutMode}
            accentTextClass="text-brown-80"
            accentBgClass="bg-brown-80"
            accentBorderClass="border-brown-80"
            onChange={(mode) =>
              dispatch(actions.setListLayoutMode({ context: PieceListContext.TRASH, mode }))
            }
          />
        </SidebarSection>
      </BuilderSidebar>

      <section className="flex-col-el relative flex-1 h-full p-4">
        <div
          ref={scrollCallbackRef}
          onScroll={onScroll}
          className="flex-col-el flex-1 w-full overflow-y-auto invisible-scrollbar"
        >
          <div className={`${LAYOUT_GRID_CLASS[layoutMode]} w-full pb-4`}>
            {visibleMaps.map((m) =>
              layoutMode === PieceLayoutMode.LIST ? (
                <MapListItem
                  key={m.piece_id}
                  piece={m}
                  context={PieceListContext.TRASH}
                  onAction={handleAction(m)}
                />
              ) : (
                <MapCard
                  key={m.piece_id}
                  piece={m}
                  context={PieceListContext.TRASH}
                  onAction={handleAction(m)}
                  compact={layoutMode === PieceLayoutMode.GRID_2}
                  heroPlay={layoutMode === PieceLayoutMode.GRID_1}
                />
              ),
            )}
          </div>
        </div>
      </section>

      <BuilderConfirmDialog
        visible={deleteTarget != null}
        title={I18n.t("MSG_BUILDER_TRASH_DELETE_CONFIRM_TITLE")}
        message={
          deleteTarget
            ? I18n.t("MSG_BUILDER_TRASH_DELETE_CONFIRM_MSG", { name: deleteTarget.name })
            : ""
        }
        close={() => setDeleteTarget(null)}
        handleConfirm={handleConfirmDelete}
      />

      {/* ゴミ箱から復活 確認 */}
      <BuilderConfirmDialog
        visible={restoreTarget != null}
        accent={BuilderModalAccent.GREEN}
        icon="restore"
        confirmIcon="restore"
        confirmLabel={I18n.t("MSG_CONFIRM_BTN")}
        confirmSound={`${Constants.assetHost}/assets/blockly/start.mp3`}
        title={I18n.t("MSG_BUILDER_RESTORE_CONFIRM_TITLE")}
        message={restoreTarget ? `「${restoreTarget.name}」` : ""}
        close={() => setRestoreTarget(null)}
        handleConfirm={handleConfirmRestore}
      />
    </div>
  );
};
